import React, { useState, useEffect } from 'react';
import { app } from './firebase';
import { getFirestore, collection, addDoc, updateDoc, deleteDoc, doc, Timestamp, arrayUnion } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

const db = getFirestore(app);

const ROLE_MAP = {
    'Infrastructure': ['Systems', 'Networking', 'Cloud', 'DevOps', 'Database'],
    'Development': ['Fullstack', 'Frontend', 'Backend', 'Mobile', 'Embedded', 'Data Engineering'],
    'Management': ['Project Management', 'Service Management', 'Product Owner/Scrum Master', 'IT Management/CTO'],
    'Security': ['Security Engineer', 'CISO/Compliance', 'Penetration Testing'],
    'Data & AI': ['Data Science', 'Business Intelligence', 'AI/Machine Learning'],
    'Support & Other': ['Support (L1/L2/L3)', 'Functional Analysis', 'Testing/QA', 'Other']
};

export default function VacanciesView({ vacancies, customers, consultants, employees, loading, logAction, isSuperAdmin, consultantDb, currentUserEmail }) {
  // --- STATE ---
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Smart Add State
  const [showSmartModal, setShowSmartModal] = useState(false);
  const [rawSmartData, setRawSmartData] = useState("");
  const [aiJsonInput, setAiJsonInput] = useState("");
  
  // View Mode (My vs All)
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'my'
  const [showArchived, setShowArchived] = useState(false);
  const [currentSpocId, setCurrentSpocId] = useState(null);

  // Modal Tab State
  const [activeTab, setActiveTab] = useState('description'); // 'description', 'candidates', 'outreach'

  // Loading state for async actions
  const [processing, setProcessing] = useState(false);

  // Determine Current User's Employee ID for Filtering
  useEffect(() => {
      if (currentUserEmail && employees.length > 0) {
          const me = employees.find(e => e.email === currentUserEmail);
          if (me) setCurrentSpocId(me.id);
      }
  }, [currentUserEmail, employees]);

  // Initial Form State
  const initialForm = {
    customerId: '', customerName: '',
    jobTitle: '', status: 'Open',
    shortDesc: '', longDesc: '',
    keywords: '', 
    region: '', country: '', workMode: 'Hybrid',
    platform: 'None', platformReference: '',
    hiringContactName: '', hiringContactEmail: '',
    spocId: '', spocName: '',
    role: 'Infrastructure', subtype: 'Systems',
    latestReactionDate: '', 
    preferredStartDate: '',
    contractType: 'Time & Material',
    createdAt: null,
    seniority: 'Medior',
    contractDuration: '',
    defaultRate: '',
    maximumRate: '',
    candidates: [],
    
    // Communication Templates
    mailSubject: 'Freelance {{jobTitle}} - {{region}} - {{duration}} months',
    mailBody: 'Hi {{name}},\n\nI came across your profile and thought you would be a great fit for our {{jobTitle}} position at {{client}}.\n\nCould you please share your updated CV if you are interested?\n\nAlso, let me know when it would suit you to grab a virtual coffee to discuss this opportunity further.\n\nBest regards,\nNettic Team',
    linkedinBody: 'Hi {{name}}, I saw your profile and thought you fit our {{jobTitle}} role perfectly. Would you be open to a quick chat and sharing your CV? Let me know when you have time for a virtual coffee!'
  };
  const [formData, setFormData] = useState(initialForm);

  // --- STAGES CONFIG ---
  const STAGES = ['Sourcing', 'CV Sent', 'Interview', 'Offer', 'Placed'];

  // --- MATCHING ALGORITHM ---
  const getMatchScore = (vacancyKeywords, candidateSkills) => {
      if (!vacancyKeywords || !candidateSkills) return 0;
      const vacKeys = vacancyKeywords.toLowerCase().split(',').map(s => s.trim()).filter(k => k);
      const candSkills = candidateSkills.toLowerCase();
      
      if(vacKeys.length === 0) return 0;

      let matches = 0;
      vacKeys.forEach(k => { if (candSkills.includes(k)) matches++; });
      return Math.round((matches / vacKeys.length) * 100);
  };

  const getRecommendations = () => {
      if (!formData.keywords) return [];
      return consultants
        .filter(c => c.status !== 'Blacklisted') 
        // Filter out candidates already applied
        .filter(c => !formData.candidates.some(app => app.id === c.id))
        .map(c => ({
            ...c, 
            score: getMatchScore(formData.keywords, c.hardSkills)
        }))
        .filter(c => c.score > 0) // Only show if there is some match
        .sort((a, b) => b.score - a.score);
  };

  // --- ACTION: ADD APPLICANT (Linked to Kanban) ---
  const handleAddApplicant = async (consultant) => {
      setProcessing(true);
      const newCandidateEntry = {
          id: consultant.id,
          name: `${consultant.firstName} ${consultant.lastName}`,
          stage: 'Sourcing',
          addedAt: new Date().toISOString()
      };

      try {
          // Use Cloud Function to ensure Kanban Item is created
          const functions = getFunctions(getApp(), 'europe-west1');
          const assignFn = httpsCallable(functions, 'assignCandidate');
          
          await assignFn({
              vacancyId: editingId,
              candidateEntry: newCandidateEntry
          });
          
          // Optimistic Update
          setFormData(prev => ({
              ...prev,
              candidates: [...prev.candidates, newCandidateEntry]
          }));
          alert(`Added ${consultant.firstName} to applicants list & created Kanban card.`);
      } catch (err) {
          console.error(err);
          alert("Error adding applicant: " + err.message);
      } finally {
          setProcessing(false);
      }
  };

  // --- ACTION: UPDATE STAGE & PLACEMENT LOGIC ---
  const updateCandidateStage = async (candId, newStage) => {
      if (!window.confirm(`Move candidate to ${newStage}?`)) return;
      setProcessing(true);

      try {
          // 1. Update Vacancy Candidate List
          const updatedCandidates = formData.candidates.map(c => 
              c.id === candId ? { ...c, stage: newStage } : c
          );
          
          let vacancyUpdates = { candidates: updatedCandidates };
          
          // 2. HANDLE PLACEMENT AUTOMATION
          if (newStage === 'Placed') {
              const cand = consultants.find(c => c.id === candId);
              
              const startDate = formData.preferredStartDate || new Date().toISOString().split('T')[0];
              const durationMonths = parseInt(formData.contractDuration) || 6;
              const endDateObj = new Date(startDate);
              endDateObj.setMonth(endDateObj.getMonth() + durationMonths);
              const endDate = endDateObj.toISOString().split('T')[0];

              // A. Create Active Contract
              await addDoc(collection(db, "active_contracts"), {
                  consultantId: candId,
                  consultantName: cand ? `${cand.firstName} ${cand.lastName}` : 'Unknown',
                  vacancyId: editingId,
                  jobTitle: formData.jobTitle,
                  client: formData.customerName,
                  startDate: startDate,
                  endDate: endDate,
                  status: 'Active',
                  dayRate: 0, 
                  costRate: 0,
                  history: [{ type: 'Created', date: new Date().toISOString(), details: 'Contract created from vacancy placement' }]
              });

              // B. Update Consultant Status
              await updateDoc(doc(db, "consultant_db", candId), {
                  status: 'Placed', 
                  availabilityDate: endDate
              });

              // C. Close Vacancy
              vacancyUpdates.status = 'Closed'; // Mark vacancy as filled
              vacancyUpdates.outcome = `Placed ${cand?.lastName}`;
              
              alert("Candidate Placed! Active Contract created, Consultant status updated, and Vacancy closed.");
          }

          // Commit Updates
          await updateDoc(doc(db, "vacancies", editingId), vacancyUpdates);
          
          // Update Local State
          setFormData(prev => ({ ...prev, ...vacancyUpdates }));

      } catch (err) {
          console.error("Error updating stage:", err);
          alert("Failed to update stage.");
      } finally {
          setProcessing(false);
      }
  };

  const handleRejectCandidate = async (candidateEntry) => {
      if (!window.confirm(`Reject ${candidateEntry.name}? This will mark them as rejected.`)) return;

      const updatedCandidates = formData.candidates.map(c => 
          c.id === candidateEntry.id ? { ...c, stage: 'Rejected' } : c
      );
      
      try {
          await updateDoc(doc(db, "vacancies", editingId), { candidates: updatedCandidates });
          setFormData(prev => ({ ...prev, candidates: updatedCandidates }));

          // Draft Rejection Email
          const candidateProfile = consultants.find(c => c.id === candidateEntry.id);
          if (candidateProfile && candidateProfile.email) {
              const subject = `Update regarding your application for ${formData.jobTitle}`;
              const body = `Dear ${candidateEntry.name},\n\nThank you for your interest in the ${formData.jobTitle} position at ${formData.customerName}.\n\nAfter careful consideration, we have decided not to proceed with your application at this time.\n\nBest regards,\nThe Nettic Team`;
              window.location.href = `mailto:${candidateProfile.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
          }
      } catch (err) {
          console.error(err);
      }
  };

  // --- COMMUNICATION HANDLER ---
  const handleSendOutreach = async (type, consultant) => {
      let subject = formData.mailSubject
        .replace('{{jobTitle}}', formData.jobTitle)
        .replace('{{client}}', formData.customerName)
        .replace('{{name}}', consultant.firstName);

      let body = (type === 'Mail' ? formData.mailBody : formData.linkedinBody)
        .replace('{{jobTitle}}', formData.jobTitle)
        .replace('{{client}}', formData.customerName)
        .replace('{{name}}', consultant.firstName);

      if (type === 'Mail') {
          window.location.href = `mailto:${consultant.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      } else {
          navigator.clipboard.writeText(body);
          alert(`LinkedIn message for ${consultant.firstName} copied to clipboard!`);
      }

      try {
          const logEntry = {
              vacancyId: editingId, 
              vacancyTitle: formData.jobTitle,
              type: type, 
              date: new Date().toISOString(),
              sentBy: 'User' 
          };

          await updateDoc(doc(db, "consultant_db", consultant.id), {
              communicationLog: arrayUnion(logEntry),
              outreachStatus: 'Reached Out'
          });

          logAction("Outreach Sent", `Sent ${type} to ${consultant.firstName} for ${formData.jobTitle}`);
      } catch (err) {
          console.error("Error logging outreach:", err);
      }
  };

  const hasBeenContacted = (consultant, type) => {
      if (!consultant.communicationLog || !editingId) return false;
      return consultant.communicationLog.some(log => 
          log.vacancyId === editingId && log.type === type
      );
  };

  // --- HANDLERS ---
  const handleCustomerChange = (e) => {
    const cust = customers.find(c => c.id === e.target.value);
    setFormData({ ...formData, customerId: cust?.id || '', customerName: cust?.companyName || '' });
  };

  const handleSpocChange = (e) => {
    const emp = employees.find(ep => ep.id === e.target.value);
    setFormData({ ...formData, spocId: emp?.id || '', spocName: emp?.name || '' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const dataToSave = { ...formData };
      Object.keys(dataToSave).forEach(key => dataToSave[key] === undefined && delete dataToSave[key]);

      if (isEditing) {
        await updateDoc(doc(db, "vacancies", editingId), dataToSave);
      } else {
        dataToSave.createdAt = Timestamp.now(); 
        await addDoc(collection(db, "vacancies"), dataToSave);
      }
      setShowModal(false);
      setFormData(initialForm);
      setIsEditing(false);
      setEditingId(null);
    } catch (err) {
      console.error(err);
      alert("Error saving vacancy.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Permanently delete this vacancy?")) return;
    try { await deleteDoc(doc(db, "vacancies", id)); } catch (err) { console.error(err); }
  };

  const handleArchive = async (id, currentStatus) => {
      if(!window.confirm("Archive this vacancy?")) return;
      try {
          const newStatus = currentStatus === 'Archived' ? 'Open' : 'Archived';
          await updateDoc(doc(db, "vacancies", id), { status: newStatus });
          if(showModal && editingId === id) {
              setFormData(prev => ({...prev, status: newStatus}));
          }
      } catch (err) { console.error(err); }
  };

  const openModal = (vac = null) => {
    if (vac) {
      setFormData({ ...initialForm, ...vac });
      setEditingId(vac.id);
      setIsEditing(true);
    } else {
      setFormData(initialForm);
      setIsEditing(false);
      setEditingId(null);
    }
    setActiveTab('description');
    setShowModal(true);
  };

  // --- SMART CREATE LOGIC ---
  const generateAiPrompt = () => {
    if (!rawSmartData) return "Please paste the vacancy data first.";
    
    return `Act as a professional recruitment data analyst for Nettic. I will provide you with a raw vacancy description in Dutch or English.
Your task is to parse this information and generate a JSON object that matches our database schema.

### SCHEMA CONSTRAINTS:
1. "jobTitle": Clear, professional title.
2. "role" & "subtype": Map to one of these: ${JSON.stringify(ROLE_MAP)}
3. "shortDesc": A very brief teaser for candidates (STRICT MAX 7 WORDS).
4. "longDesc": A clean, structured version of the full description (Markdown supported).
5. "keywords": Comma-separated list of technical skills (max 10).
6. "region": Extract location (e.g., Brussels, Aalst, Remote).
7. "seniority": "Medior" or "Senior" or "Lead".
8. "preferredStartDate": Extract start date if mentioned (format YYYY-MM-DD).
9. "latestReactionDate": Extract deadline for submission if mentioned (format YYYY-MM-DD).
10. "contractDuration": Number of months (e.g. "6").
11. "defaultRate": Extract internal target daily rate if mentioned (number only).
12. "maximumRate": Extract maximum daily rate if mentioned (number only).
13. "platform": Extract platform name if mentioned (e.g. Staff-it, Pro-unity, Connecting Expertise).
14. "platformReference": Extract the unique vacancy reference ID from the platform.
15. "mailSubject": MUST be in this EXACT format: "Freelance [jobTitle] - [region] - [contractDuration] months" (If duration > 12, use "12 months +").
16. "mailBody": A personalized email. Always ask to share their updated CV and invite them for a virtual coffee to discuss further.
17. "linkedinBody": A short LinkedIn message. Always ask to share their CV and invite them for a virtual coffee.

### STRICT RULES:
- If a value is not found, use an empty string "" instead of null.
- In the "mailBody" and "linkedinBody", NEVER mention the name of the end-client/customer. 
- In the "mailBody" and "linkedinBody", NEVER mention any specific rates or price agreements (defaultRate or maximumRate). Keep pricing for personal discussions.
- Use generic terms like "a global mobility customer", "a Flemish government agency", or "our client in the [Industry] sector".
- The signature email is: ${currentUserEmail || '[Your Email]'}

### EMAIL FORMAT EXAMPLE:
Hi {{name}},

For a [Industry Description] customer in [Location], we are searching for a freelance [Job Title] for a [Project Context].

[Brief description of why it's interesting]

Responsibilities:
- [Point 1]
- ...

Profile:
- [Point 1]
- ...

Additional Details:
SENIORITY: [Seniority]
START DATE: [Start Date]
DURATION: [Duration]
CONTRACT: Freelance
MAIN OFFICE LOCATION: [Region]
ONSITE POLICY: [e.g. 3 days per week]

If you are interested, please forward your updated resume to ${currentUserEmail || '[Your Email]'} to be considered.

---
RAW DATA:
${rawSmartData}

---
OUTPUT ONLY THE JSON OBJECT.`;
  };

  const handleImportAiJson = () => {
      try {
          // Clean the string in case AI added markdown blocks ```json ... ```
          let cleanJson = aiJsonInput.trim();
          if (cleanJson.startsWith("```json")) cleanJson = cleanJson.replace(/```json/g, "").replace(/```/g, "");
          else if (cleanJson.startsWith("```")) cleanJson = cleanJson.replace(/```/g, "");

          const parsed = JSON.parse(cleanJson);
          
          // Sanitize: Ensure no null values (React inputs hate null)
          Object.keys(parsed).forEach(key => {
              if (parsed[key] === null) parsed[key] = "";
          });

          // Merge with initial form to ensure all fields exist
          const merged = { 
              ...initialForm, 
              ...parsed,
              status: 'Open', // Force Open status for new AI-created vacancies
              spocId: currentSpocId || '',
              spocName: employees.find(e => e.id === currentSpocId)?.name || ''
          };

          setFormData(merged);
          setIsEditing(false);
          setEditingId(null);
          setShowSmartModal(false);
          setRawSmartData("");
          setAiJsonInput("");
          setShowModal(true); // Open the main modal with pre-filled info
          setActiveTab('description');
      } catch (err) {
          alert("Invalid JSON format. Please make sure you copied the entire object correctly from Gemini/ChatGPT.");
          console.error(err);
      }
  };

  // --- FILTERING ---
  const allFiltered = vacancies.filter(v => {
    const matchesSearch = (v.jobTitle + v.customerName + v.keywords).toLowerCase().includes(searchTerm.toLowerCase());
    
    // Auto-Archive Logic: surpassed deadline AND no candidates
    // We only auto-archive vacancies that are NOT 'Open'. 
    // This ensures that if a user sets a vacancy to 'Open', it always shows in the main tab.
    const today = new Date().toISOString().split('T')[0];
    const hasPassedDeadline = v.latestReactionDate && v.latestReactionDate !== "" && v.latestReactionDate < today;
    const hasNoCandidates = !v.candidates || v.candidates.length === 0;
    const isAutoArchived = v.status !== 'Open' && v.status !== 'Archived' && hasPassedDeadline && hasNoCandidates;
    
    const isExplicitlyArchived = v.status === 'Archived';
    const isArchived = isExplicitlyArchived || isAutoArchived;

    const matchesViewMode = viewMode === 'all' ? true : (v.spocId === currentSpocId);
    
    if (showArchived) {
        return matchesSearch && matchesViewMode && isArchived;
    }
    return matchesSearch && matchesViewMode && !isArchived; 
  }).sort((a, b) => {
    const dateA = a.createdAt?.seconds || 0;
    const dateB = b.createdAt?.seconds || 0;
    return dateB - dateA;
  });

  return (
    <div className="container-fluid p-0">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-main">Vacancies</h2>
          <p className="text-muted-theme">Track opportunities, manage pipeline, and hire.</p>
        </div>
        <div className="d-flex gap-3 align-items-center">
          {/* VIEW MODE TOGGLE */}
          <div className="btn-group">
              <button className={`btn ${viewMode === 'my' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setViewMode('my')}>My Vacancies</button>
              <button className={`btn ${viewMode === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setViewMode('all')}>All Vacancies</button>
          </div>

          <button 
              className={`btn ${showArchived ? 'btn-warning' : 'btn-outline-warning'}`} 
              onClick={() => setShowArchived(!showArchived)}
          >
              <i className={`bi ${showArchived ? 'bi-archive-fill' : 'bi-archive'} me-1`}></i>
              {showArchived ? 'Hide Archived' : 'Show Archived'}
          </button>

          <div className="d-flex gap-2">
              <input type="text" className="form-control" placeholder="Search vacancies..." onChange={(e) => setSearchTerm(e.target.value)} />
              <div className="dropdown">
                  <button className="btn btn-primary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                      <i className="bi bi-plus-lg"></i> Add Vacancy
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end shadow border-theme">
                      <li><button className="dropdown-item" onClick={() => openModal()}><i className="bi bi-pencil-square me-2"></i> Manual Entry</button></li>
                      <li><button className="dropdown-item" onClick={() => setShowSmartModal(true)}><i className="bi bi-robot me-2 text-primary"></i> Smart AI Create</button></li>
                  </ul>
              </div>
          </div>
        </div>
      </div>

      {/* GRID VIEW */}
      {loading ? <div className="text-main">Loading...</div> : (
        <div className="row g-4">
            {allFiltered.length > 0 ? allFiltered.map(v => {
                // Find SPOC data for picture
                const spoc = employees.find(e => e.id === v.spocId);
                // Parse skills (first 5)
                const skills = v.keywords ? v.keywords.split(',').slice(0, 5).map(s => s.trim()) : [];

                return (
                    <div key={v.id} className="col-lg-6 col-xl-4">
                      <div className="card h-100 bg-card border-theme shadow-sm">
                        <div className="card-body d-flex flex-column">
                          <div className="d-flex justify-content-between mb-2">
                            <span className="badge bg-primary bg-opacity-10 text-primary">{v.customerName}</span>
                            <span className={`badge ${v.status === 'Open' ? 'bg-success' : 'bg-secondary'}`}>{v.status}</span>
                          </div>
                          
                          <h5 className="fw-bold mb-1 text-main text-truncate" title={v.jobTitle}>{v.jobTitle}</h5>
                          <small className="text-muted-theme d-block mb-3"><i className="bi bi-geo-alt"></i> {v.region}</small>

                          {/* SKILLS TAGS */}
                          <div className="mb-3 d-flex flex-wrap gap-1">
                              {skills.length > 0 ? skills.map((skill, idx) => (
                                  <span key={idx} className="badge bg-secondary bg-opacity-25 text-muted-theme border border-theme" style={{fontSize: '0.65rem'}}>
                                      {skill}
                                  </span>
                              )) : (
                                  <span className="small text-muted-theme fst-italic">No skills listed</span>
                              )}
                          </div>
                          
                          <p className="text-muted-theme small mb-3 flex-grow-1 text-truncate">{v.shortDesc}</p>
                          
                          {/* FOOTER: APPLICANTS & SPOC */}
                          <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top border-theme">
                              <span className="badge bg-info text-dark">
                                  {v.candidates?.length || 0} Applicants
                              </span>
                              
                              <div className="d-flex align-items-center" title={spoc ? spoc.name : "Unassigned"}>
                                  {spoc && spoc.imageFile ? (
                                      <img src={spoc.imageFile} className="rounded-circle border border-theme" style={{width: 24, height: 24, objectFit: 'cover'}} alt="SPOC" />
                                  ) : (
                                      <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white small" style={{width: 24, height: 24}}>
                                          {spoc ? spoc.name.charAt(0) : '?'}
                                      </div>
                                  )}
                                  <span className="ms-2 text-muted-theme small" style={{fontSize: '0.75rem'}}>
                                      {spoc ? spoc.name.split(' ')[0] : 'Unassigned'}
                                  </span>
                              </div>
                          </div>
                          
                          <div className="d-flex gap-2 mt-3">
                              <button className="btn btn-sm btn-outline-primary flex-grow-1" onClick={() => openModal(v)}>Manage</button>
                              <button className="btn btn-sm btn-outline-warning" onClick={() => handleArchive(v.id, v.status)} title="Archive"><i className="bi bi-archive"></i></button>
                              {isSuperAdmin && <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(v.id)}><i className="bi bi-trash"></i></button>}
                          </div>
                        </div>
                      </div>
                    </div>
                );
            }) : (
                <div className="col-12 text-center py-5 text-muted-theme">
                    <i className="bi bi-search fs-1 d-block mb-3"></i>
                    No vacancies found in this view.
                </div>
            )}
        </div>
      )}

      {/* EXPANDED MODAL LAYOUT */}
      {showModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.6)'}}>
          <div className="modal-dialog modal-fullscreen-lg-down modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content bg-card border-theme shadow-lg h-100">
              
              {/* HEADER - FIXED OVERLAP */}
              <div className="modal-header border-bottom border-theme py-3 px-4 d-flex justify-content-between align-items-center">
                <div className="overflow-hidden me-3" style={{flex: 1}}>
                    <h3 className="modal-title fw-bold text-main mb-0 text-truncate" title={isEditing ? formData.jobTitle : "New Vacancy"}>
                        {isEditing ? formData.jobTitle : "New Vacancy"}
                    </h3>
                    <p className="text-muted-theme mb-0 text-truncate small">{formData.customerName}</p>
                </div>
                <div className="d-flex gap-2 flex-shrink-0">
                    <button className="btn btn-success d-none d-sm-inline-block" onClick={handleSave}>{isEditing ? 'Save' : 'Create'}</button>
                    <button className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>
                        <span className="d-none d-sm-inline">Close</span>
                        <i className="bi bi-x-lg d-sm-none"></i>
                    </button>
                </div>
              </div>

              <div className="modal-body p-0 h-100">
                <div className="row g-0 h-100">
                    
                    {/* LEFT COLUMN: METADATA FORM */}
                    <div className="col-lg-3 border-end border-theme p-3 p-md-4 bg-input overflow-auto">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="text-uppercase text-muted-theme small fw-bold mb-0">Vacancy Details</h6>
                            <button className="btn btn-sm btn-success d-sm-none" onClick={handleSave}>Save</button>
                        </div>
                        
                        <div className="mb-3">
                            <label className="small text-muted-theme">Job Title</label>
                            <input className="form-control" value={formData.jobTitle || ''} onChange={e => setFormData({...formData, jobTitle: e.target.value})} />
                        </div>
                        <div className="mb-3">
                            <label className="small text-muted-theme">Customer</label>
                            <select className="form-select" value={formData.customerId || ''} onChange={handleCustomerChange}>
                                <option value="">Select...</option>
                                {customers.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                            </select>
                        </div>
                        <div className="row g-2 mb-3">
                            <div className="col-6">
                                <label className="small text-muted-theme">Status</label>
                                <select className="form-select" value={formData.status || ''} onChange={e => setFormData({...formData, status: e.target.value})}>
                                    <option>Open</option><option>Closed</option><option>Archived</option>
                                </select>
                            </div>
                            <div className="col-6">
                                <label className="small text-muted-theme">Seniority</label>
                                <select className="form-select" value={formData.seniority || ''} onChange={e => setFormData({...formData, seniority: e.target.value})}>
                                    <option>Medior</option><option>Senior</option><option>Lead</option>
                                </select>
                            </div>
                        </div>

                        <div className="row g-2 mb-3">
                            <div className="col-6">
                                <label className="small text-muted-theme">Platform</label>
                                <select className="form-select" value={formData.platform || 'None'} onChange={e => setFormData({...formData, platform: e.target.value})}>
                                    <option value="None">None</option>
                                    <option value="Staff-it">Staff-it</option>
                                    <option value="Connecting Expertise">Connecting Expertise</option>
                                    <option value="Pro-unity">Pro-unity</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div className="col-6">
                                <label className="small text-muted-theme">Platform Ref</label>
                                <input className="form-control" value={formData.platformReference || ''} onChange={e => setFormData({...formData, platformReference: e.target.value})} placeholder="REF-123" />
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="small text-muted-theme">Keywords (Skills)</label>
                            <input className="form-control" value={formData.keywords || ''} onChange={e => setFormData({...formData, keywords: e.target.value})} placeholder="Java, React, AWS..." />
                        </div>

                        <div className="row g-2 mb-3">
                            <div className="col-6">
                                <label className="small text-muted-theme">Preferred Start</label>
                                <input type="date" className="form-control" value={formData.preferredStartDate || ''} onChange={e => setFormData({...formData, preferredStartDate: e.target.value})} />
                            </div>
                            <div className="col-6">
                                <label className="small text-muted-theme">Reaction Deadline</label>
                                <input type="date" className="form-control" value={formData.latestReactionDate || ''} onChange={e => setFormData({...formData, latestReactionDate: e.target.value})} />
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="small text-muted-theme">Nettic SPOC</label>
                            <select className="form-select" value={formData.spocId || ''} onChange={handleSpocChange}>
                                <option value="">Unassigned</option>
                                {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                            </select>
                        </div>

                        <div className="row g-2 mb-3">
                            <div className="col-6">
                                <label className="small text-muted-theme">Role Category</label>
                                <select 
                                    className="form-select" 
                                    value={formData.role || 'Infrastructure'} 
                                    onChange={e => setFormData({...formData, role: e.target.value, subtype: ROLE_MAP[e.target.value][0]})}
                                >
                                    {Object.keys(ROLE_MAP).map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>
                            <div className="col-6">
                                <label className="small text-muted-theme">Subtype</label>
                                <select 
                                    className="form-select" 
                                    value={formData.subtype || ''} 
                                    onChange={e => setFormData({...formData, subtype: e.target.value})}
                                >
                                    {ROLE_MAP[formData.role || 'Infrastructure'].map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="row g-2 mb-3">
                            <div className="col-6">
                                <label className="small text-muted-theme">Default Rate (€)</label>
                                <input type="number" className="form-control" value={formData.defaultRate || ''} onChange={e => setFormData({...formData, defaultRate: e.target.value})} placeholder="750" />
                            </div>
                            <div className="col-6">
                                <label className="small text-muted-theme">Max Rate (€)</label>
                                <input type="number" className="form-control" value={formData.maximumRate || ''} onChange={e => setFormData({...formData, maximumRate: e.target.value})} placeholder="900" />
                            </div>
                        </div>
                        
                        <div className="p-3 border border-theme rounded bg-card mt-4">
                            <h6 className="text-main small fw-bold">Hiring Manager</h6>
                            <input className="form-control mb-2" placeholder="Name" value={formData.hiringContactName || ''} onChange={e => setFormData({...formData, hiringContactName: e.target.value})} />
                            <input className="form-control" placeholder="Email" value={formData.hiringContactEmail || ''} onChange={e => setFormData({...formData, hiringContactEmail: e.target.value})} />
                        </div>

                        {isEditing && (
                            <div className="mt-4 pt-3 border-top border-theme">
                                <button className="btn btn-sm btn-outline-warning w-100" onClick={() => handleArchive(editingId, formData.status)}>
                                    <i className="bi bi-archive me-2"></i>{formData.status === 'Archived' ? 'Restore' : 'Archive'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* RIGHT COLUMN: TABS & CONTENT */}
                    <div className="col-lg-9 p-3 p-md-4 bg-card d-flex flex-column h-100 overflow-hidden">
                        <ul className="nav nav-tabs border-theme mb-3 mb-md-4 flex-nowrap overflow-auto flex-shrink-0">
                            <li className="nav-item"><button className={`nav-link text-nowrap ${activeTab==='description'?'active bg-input text-primary border-theme border-bottom-0':'text-muted-theme border-transparent'}`} onClick={()=>setActiveTab('description')}>Description</button></li>
                            <li className="nav-item"><button className={`nav-link text-nowrap ${activeTab==='candidates'?'active bg-input text-primary border-theme border-bottom-0':'text-muted-theme border-transparent'}`} onClick={()=>setActiveTab('candidates')}>Applicants ({formData.candidates?.length || 0})</button></li>
                            <li className="nav-item"><button className={`nav-link text-nowrap ${activeTab==='outreach'?'active bg-input text-primary border-theme border-bottom-0':'text-muted-theme border-transparent'}`} onClick={()=>setActiveTab('outreach')}>Outreach & Recs</button></li>
                        </ul>

                        <div className="flex-grow-1 overflow-auto pe-1 pe-md-2 pb-5">
                            {/* TAB: DESCRIPTION */}
                            {activeTab === 'description' && (
                                <>
                                    <div className="mb-3">
                                        <label className="small text-muted-theme">Short Description (Max 7 words)</label>
                                        <textarea className="form-control" rows="2" value={formData.shortDesc || ''} onChange={e => setFormData({...formData, shortDesc: e.target.value})}></textarea>
                                    </div>
                                    <div className="mb-3">
                                        <label className="small text-muted-theme">Full Description</label>
                                        <textarea className="form-control" rows="15" value={formData.longDesc || ''} onChange={e => setFormData({...formData, longDesc: e.target.value})}></textarea>
                                    </div>
                                </>
                            )}

                            {/* TAB: CANDIDATES PIPELINE */}
                            {activeTab === 'candidates' && (
                                <div className="list-group">
                                    {formData.candidates && formData.candidates.length > 0 ? formData.candidates.map((cand, idx) => (
                                        <div key={idx} className="list-group-item bg-input border-theme d-flex justify-content-between align-items-center p-3 mb-2 rounded shadow-sm">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white fw-bold" style={{width: 40, height: 40}}>
                                                    {cand.name.charAt(0)}
                                                </div>
                                                <div>
                                                    <div className="fw-bold text-main">{cand.name}</div>
                                                    <small className="text-muted-theme">Stage: <span className="text-primary">{cand.stage}</span></small>
                                                </div>
                                            </div>
                                            
                                            <div className="d-flex gap-2">
                                                {/* PIPELINE ACTIONS */}
                                                {cand.stage !== 'Rejected' && cand.stage !== 'Placed' && (
                                                    <div className="btn-group">
                                                        {STAGES.map(stage => (
                                                            <button 
                                                                key={stage} 
                                                                className={`btn btn-sm ${cand.stage === stage ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                                onClick={() => updateCandidateStage(cand.id, stage)}
                                                                disabled={processing}
                                                            >
                                                                {stage}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                                
                                                {cand.stage === 'Placed' && <span className="badge bg-success align-self-center">Hired</span>}
                                                {cand.stage === 'Rejected' && <span className="badge bg-danger align-self-center">Rejected</span>}

                                                {cand.stage !== 'Rejected' && cand.stage !== 'Placed' && (
                                                    <button className="btn btn-sm btn-outline-danger ms-2" onClick={() => handleRejectCandidate(cand)}>
                                                        Reject
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="text-center py-5 text-muted-theme">
                                            <i className="bi bi-people fs-1 d-block mb-3"></i>
                                            <p>No active applicants yet.</p>
                                            <small>Go to "Outreach & Recommendations" to add candidates.</small>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* TAB: OUTREACH & RECOMMENDATIONS */}
                            {activeTab === 'outreach' && (
                                <>
                                    {/* 1. COMMUNICATION CONFIG */}
                                    <div className="mb-4 p-3 bg-input border border-theme rounded">
                                        <h6 className="text-primary fw-bold mb-3"><i className="bi bi-chat-dots me-2"></i> Outreach Templates</h6>
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <label className="small text-muted-theme">Mail Subject</label>
                                                <input className="form-control" value={formData.mailSubject || ''} onChange={e => setFormData({...formData, mailSubject: e.target.value})} />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="small text-muted-theme">Mail Body</label>
                                                <textarea className="form-control" rows="4" value={formData.mailBody || ''} onChange={e => setFormData({...formData, mailBody: e.target.value})}></textarea>
                                                <small className="text-muted-theme" style={{fontSize: '0.75rem'}}>Vars: {'{{name}}'}, {'{{client}}'}, {'{{jobTitle}}'}</small>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="small text-muted-theme">LinkedIn Message</label>
                                                <textarea className="form-control" rows="4" value={formData.linkedinBody || ''} onChange={e => setFormData({...formData, linkedinBody: e.target.value})}></textarea>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 2. RECOMMENDATIONS LIST */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="text-main fw-bold mb-0">Recommended Consultants ({getRecommendations().length})</h6>
                                        <small className="text-muted-theme">Ranked by keyword match</small>
                                    </div>
                                    
                                    <div className="list-group">
                                        {getRecommendations().map(c => {
                                            const mailSent = hasBeenContacted(c, 'Mail');
                                            const linkedinSent = hasBeenContacted(c, 'LinkedIn');

                                            return (
                                                <div key={c.id} className="list-group-item bg-card border-theme mb-3 rounded shadow-sm p-3">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        {/* PROFILE INFO */}
                                                        <div className="d-flex align-items-center gap-3">
                                                            {c.imageFile ? (
                                                                <img src={c.imageFile} className="rounded-circle object-fit-cover border border-theme" style={{width: 60, height: 60}} alt="Profile" />
                                                            ) : (
                                                                <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white fw-bold" style={{width: 60, height: 60, fontSize: 24}}>{c.firstName?.charAt(0)}</div>
                                                            )}
                                                            <div>
                                                                <div className="fw-bold text-main fs-5">{c.firstName} {c.lastName}</div>
                                                                <div className="d-flex gap-2 align-items-center mb-1">
                                                                    <span className={`badge ${c.score > 75 ? 'bg-success' : 'bg-warning text-dark'}`}>{c.score}% Match</span>
                                                                    <small className="text-muted-theme">{c.hardSkills?.substring(0, 40)}...</small>
                                                                </div>
                                                                {c.pools && (
                                                                    <div>
                                                                        {c.pools.split(',').slice(0,3).map(tag => (
                                                                            <span key={tag} className="badge bg-secondary bg-opacity-25 text-muted-theme me-1 border border-theme" style={{fontSize: '0.65rem'}}>{tag.trim()}</span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* ACTIONS */}
                                                        <div className="d-flex flex-column gap-2 align-items-end">
                                                            <div className="d-flex gap-2">
                                                                <button 
                                                                    className={`btn btn-sm ${mailSent ? 'btn-success disabled' : 'btn-outline-primary'}`} 
                                                                    onClick={() => !mailSent && handleSendOutreach('Mail', c)}
                                                                    title={mailSent ? "Mail already sent" : "Send Mail"}
                                                                >
                                                                    <i className={`bi ${mailSent ? 'bi-check-lg' : 'bi-envelope'}`}></i> {mailSent ? 'Sent' : 'Mail'}
                                                                </button>

                                                                <button 
                                                                    className={`btn btn-sm ${linkedinSent ? 'btn-success disabled' : 'btn-outline-info'}`} 
                                                                    onClick={() => !linkedinSent && handleSendOutreach('LinkedIn', c)}
                                                                    title={linkedinSent ? "LinkedIn message sent" : "Copy LinkedIn Message"}
                                                                >
                                                                    <i className={`bi ${linkedinSent ? 'bi-check-lg' : 'bi-linkedin'}`}></i> {linkedinSent ? 'Sent' : 'LinkedIn'}
                                                                </button>
                                                            </div>
                                                            <button 
                                                                className="btn btn-sm btn-outline-secondary w-100" 
                                                                onClick={() => handleAddApplicant(c)}
                                                                disabled={processing}
                                                            >
                                                                <i className="bi bi-plus-lg me-1"></i> Add to Applicants
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {getRecommendations().length === 0 && (
                                            <div className="text-center py-5">
                                                <p className="text-muted-theme">No matching consultants found for these keywords.</p>
                                                <small className="text-muted-theme">Try updating the "Keywords" field in vacancy details.</small>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* SMART AI MODAL */}
      {showSmartModal && (
          <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.8)'}}>
              <div className="modal-dialog modal-lg modal-dialog-centered">
                  <div className="modal-content border-theme shadow-lg">
                      <div className="modal-header">
                          <h5 className="modal-title text-main">
                              <i className="bi bi-robot me-2 text-primary"></i>
                              Smart Vacancy Creator
                          </h5>
                          <button className="btn-close" onClick={() => setShowSmartModal(false)}></button>
                      </div>
                      <div className="modal-body">
                          <div className="row g-4">
                              {/* STEP 1: Paste Raw Data */}
                              <div className="col-12">
                                  <label className="form-label fw-bold text-main">Step 1: Paste Raw Vacancy Data</label>
                                  <textarea 
                                      className="form-control bg-input" 
                                      rows="6" 
                                      placeholder="Paste the description, requirements, or email text here..."
                                      value={rawSmartData}
                                      onChange={e => setRawSmartData(e.target.value)}
                                  ></textarea>
                              </div>

                              {/* STEP 2: Copy Prompt */}
                              {rawSmartData && (
                                  <div className="col-12">
                                      <label className="form-label fw-bold text-main">Step 2: Copy this Prompt to Gemini/ChatGPT</label>
                                      <div className="position-relative">
                                          <textarea 
                                              className="form-control bg-dark text-info small" 
                                              rows="4" 
                                              readOnly 
                                              value={generateAiPrompt()}
                                          ></textarea>
                                          <button 
                                              className="btn btn-sm btn-light position-absolute top-0 end-0 m-2"
                                              onClick={() => {
                                                  navigator.clipboard.writeText(generateAiPrompt());
                                                  alert("Prompt copied to clipboard!");
                                              }}
                                          >
                                              <i className="bi bi-clipboard"></i> Copy
                                          </button>
                                      </div>
                                      <small className="text-muted-theme mt-1 d-block">This prompt ensures the AI generates structured data and privacy-safe emails.</small>
                                  </div>
                              )}

                              {/* STEP 3: Paste JSON */}
                              <div className="col-12">
                                  <label className="form-label fw-bold text-main">Step 3: Paste the AI Response (JSON)</label>
                                  <textarea 
                                      className="form-control bg-input" 
                                      rows="6" 
                                      placeholder='Paste the JSON object starting with { "jobTitle": ... }'
                                      value={aiJsonInput}
                                      onChange={e => setAiJsonInput(e.target.value)}
                                  ></textarea>
                              </div>
                          </div>
                      </div>
                      <div className="modal-footer border-theme">
                          <button className="btn btn-outline-secondary" onClick={() => setShowSmartModal(false)}>Cancel</button>
                          <button className="btn btn-primary" onClick={handleImportAiJson} disabled={!aiJsonInput}>
                              <i className="bi bi-magic me-2"></i> Import & Pre-fill Form
                          </button>
                      </div>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
}